<script setup lang="ts">
import { computed, ref, watch, onUnmounted, nextTick } from 'vue'
import {
  RIGHT_PANEL_TOAST_VIEWPORT_PRESET_FLOATING,
  useToastViewportScope,
} from '@/composables/toast'
import { useI18n } from 'vue-i18n'
import { useToast } from '@ai/ui-components/toast'
import { useRouter } from 'vue-router'
import { useLanguages } from '@/composables/translations/useLanguages'
import { useKnowledgeApi } from '@/composables/api/useKnowledgeApi'
import { useConfigurationDataQuery } from '@/composables/queries/configurations/useConfigurationDataQuery'
import type { Configurator, CustomerIntent } from 'shared/src'

interface Props {
  configurator: Configurator
  locale: string
}

const props = defineProps<Props>()
const open = defineModel<boolean>('open', { default: false })

useToastViewportScope(open, RIGHT_PANEL_TOAST_VIEWPORT_PRESET_FLOATING)

const { t, locale: i18nLocale } = useI18n({ useScope: 'global' })
const router = useRouter()
const toast = useToast()
const { documentOptions } = useKnowledgeApi()
const { languageLabel } = useLanguages()

const openId = computed(() => (open.value ? props.configurator.id : null))
const { data: config, isPending, isError } = useConfigurationDataQuery(openId)

const enabledLanguages = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const voiceIds = config.value?.settings?.general_information?.voice_ids
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const supported = config.value?.settings?.general_information?.supported_languages ?? []
  if (voiceIds && Object.keys(voiceIds).length > 0) {
    return Object.keys(voiceIds)
  }
  const defaultLang = supported.find((l) => l.default)?.code ?? undefined
  return defaultLang ? [defaultLang] : []
})

const flagName = (code: string): string =>
  code === 'en' ? 'us' : (code.split('-')[1] ?? code).toLowerCase()

const mainSkills = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const common = config.value?.tools?.common
  const keys = ['qualify_call', 'answer_with_rag', 'step_validation'] as const
  return keys.filter((key) => common?.includes(key))
})

const customerIntents = computed<CustomerIntent[]>(() => {
  return (
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    (config.value?.scenarios?.[0]?.metadata?.customer_intents as CustomerIntent[] | undefined) ?? []
  )
})

const showIntents = computed(
  () => mainSkills.value.includes('qualify_call') || mainSkills.value.includes('step_validation'),
)
const showDocuments = computed(() => mainSkills.value.includes('answer_with_rag'))

const sectionNumbers = computed(() => {
  let n = 0
  const general = ++n
  const conversational = ++n
  const intents = showIntents.value ? ++n : null
  const knowledge = showDocuments.value ? ++n : null
  const callLimits = ++n
  return { general, conversational, intents, knowledge, callLimits }
})

const documentNames = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const ids = config.value?.settings?.knowledge?.document_ids ?? []
  return (
    ids
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      .map((id) => documentOptions.value.find((opt) => opt.value === id)?.label || id)
      .filter(Boolean)
  )
})

const docsContainerRef = ref<HTMLElement | null>(null)
const docsOverflowCount = ref(0)
const docsLabelWidth = ref(0)
let docsObserver: ResizeObserver | null = null

const GAP_S = 8 // --spacing-s

function updateDocs() {
  const container = docsContainerRef.value
  if (!container) return
  docsLabelWidth.value = Math.floor((container.clientWidth - 2 * GAP_S) / 3)
  void nextTick(() => {
    const containerBottom = container.getBoundingClientRect().bottom
    docsOverflowCount.value = [...container.children].filter(
      (el) => (el as HTMLElement).getBoundingClientRect().bottom > containerBottom,
    ).length
  })
}

watch(docsContainerRef, (el) => {
  if (!el) return
  docsObserver = new ResizeObserver(updateDocs)
  docsObserver.observe(el)
  updateDocs()
})

watch(documentNames, () => nextTick(updateDocs))

const handleError = () => {
  open.value = false
  toast.error(t('error_occurred_try_again'), { title: t('error_occurred') })
}

watch(open, (val) => {
  if (val && isError.value) handleError()
})

watch(isError, (val) => {
  if (val) handleError()
})

const truncatedId = computed(() => {
  const id = props.configurator.id
  if (id.length <= 16) return id
  return `${id.slice(0, 8)}...${id.slice(-4)}`
})

const isCopied = ref(false)
let copyResetTimer: ReturnType<typeof setTimeout> | null = null

const copyId = async () => {
  await navigator.clipboard.writeText(props.configurator.id)
  isCopied.value = true
  if (copyResetTimer) clearTimeout(copyResetTimer)
  copyResetTimer = setTimeout(() => {
    isCopied.value = false
  }, 2000)
}

onUnmounted(() => {
  docsObserver?.disconnect()
  if (copyResetTimer) clearTimeout(copyResetTimer)
})

const goToTab = (tab: string) => {
  open.value = false
  void router.push(`/${props.locale}/configurator/${props.configurator.id}/${tab}`)
}
</script>

<template>
  <ORightPanel
    v-model:open="open"
    :title="t('agent_details')"
    floating
    panel-class="w-[600px] right-4"
  >
    <template #default>
      <div v-if="isPending" class="flex items-center justify-center h-64">
        <OSpinner size="M" />
      </div>

      <div v-else-if="config" class="flex flex-col">
        <!-- 1. General information -->
        <section class="pb-l space-y-l">
          <h3 class="typography-body-1-medium text-neutral-2">
            {{ sectionNumbers.general }}. {{ t('general_information') }}
          </h3>
          <div class="space-y-l">
            <div>
              <p class="typography-body-2-strong text-neutral-1 mb-s">{{ t('agent_name') }}</p>
              <p class="typography-body-2 text-neutral-1">
                {{ config.settings?.general_information?.agent_name || configurator.name || '-' }}
              </p>
            </div>
            <div>
              <label class="typography-body-2-strong text-neutral-1 block mb-s">{{
                t('agent_id')
              }}</label>
              <div class="flex items-center gap-[6px]">
                <OLabel :label="truncatedId" class="min-w-0" />
                <OToolTip :content="isCopied ? t('copied') : t('copy')">
                  <OButton size="S" variant="tertiary" roundedFull @click="copyId">
                    <template #icon>
                      <UiIcon
                        :clickable="false"
                        :name="isCopied ? 'positive-check' : 'copy'"
                        color="text-neutral-2"
                        size="l"
                      />
                    </template>
                  </OButton>
                </OToolTip>
              </div>
            </div>

            <div>
              <label class="typography-body-2-strong text-neutral-1 block mb-s">{{
                t('languages')
              }}</label>
              <div class="flex gap-s mt-xs">
                <OBadge
                  v-for="code in enabledLanguages.slice(0, 3)"
                  :key="code"
                  variant="secondary"
                  :content="languageLabel(i18nLocale, code)"
                  :leading-icon="flagName(code) as any"
                  truncate
                />
              </div>
              <button
                v-if="enabledLanguages.length > 3"
                class="typography-body-2-link text-primary-interactive underline cursor-pointer mt-s"
                @click="goToTab('general-information')"
                data-testid="languages-see-all"
              >
                {{ t('and_x_others', [enabledLanguages.length - 3]) }}
              </button>
            </div>
            <div>
              <label class="typography-body-2-strong text-neutral-1 block mb-s">{{
                t('main_skills.title')
              }}</label>
              <ul v-if="mainSkills.length > 0" class="space-y-s">
                <li
                  v-for="skill in mainSkills"
                  :key="skill"
                  class="typography-body-2 text-neutral-1"
                >
                  {{ t(`main_skills.${skill}`) }}
                </li>
              </ul>
              <span v-else class="typography-body-2 text-neutral-1">-</span>
            </div>
          </div>
        </section>

        <ODivider />

        <!-- 2. Conversational flow -->
        <section class="py-l space-y-l">
          <h3 class="typography-body-1-medium text-neutral-2">
            {{ sectionNumbers.conversational }}. {{ t('conversational_flow') }}
          </h3>
          <div class="grid grid-cols-2 gap-l">
            <div>
              <p class="typography-body-2-strong text-neutral-1 mb-s">{{ t('welcome_message') }}</p>
              <OSwitch
                :model-value="config.settings?.conversational_flow?.has_welcome_message ?? false"
                disabled
              />
            </div>
            <div>
              <p class="typography-body-2-strong text-neutral-1 mb-s">{{ t('wrap_up_message') }}</p>
              <OSwitch
                :model-value="config.settings?.conversational_flow?.has_wrap_up_message ?? false"
                disabled
              />
            </div>
          </div>
        </section>

        <template v-if="showIntents">
          <ODivider />

          <!-- 3. Intent recognition -->
          <section class="py-l space-y-l">
            <h3 class="typography-body-1-medium text-neutral-2">
              {{ sectionNumbers.intents }}. {{ t('intent_recognition') }}
            </h3>
            <div v-if="customerIntents.length === 0" class="typography-body-2 text-neutral-1">
              -
            </div>
            <div v-else>
              <div class="grid grid-cols-2 gap-l mb-xs">
                <span class="typography-body-2-strong text-neutral-1">{{ t('elements') }}</span>
                <span class="typography-body-2-strong text-neutral-1">{{ t('outputs') }}</span>
              </div>
              <div
                v-for="intent in customerIntents.slice(0, 3)"
                :key="intent.name"
                class="grid grid-cols-2 gap-l py-xs min-w-0"
              >
                <p class="typography-body-2 text-neutral-1 truncate">{{ intent.name }}</p>
                <p class="typography-body-2 text-neutral-1 truncate">{{ intent.output || '-' }}</p>
              </div>
              <button
                v-if="customerIntents.length > 3"
                class="typography-body-2-link text-primary-interactive underline cursor-pointer mt-s"
                @click="goToTab('intent-recognition')"
                data-testid="intents-see-all"
              >
                {{ t('and_x_others', [customerIntents.length - 3]) }}
              </button>
            </div>
          </section>
        </template>

        <template v-if="showDocuments">
          <ODivider />

          <!-- 4. Knowledge -->
          <section class="py-l space-y-l">
            <h3 class="typography-body-1-medium text-neutral-2">
              {{ sectionNumbers.knowledge }}. {{ t('knowledge') }}
            </h3>
            <div>
              <p class="typography-body-2-strong text-neutral-1 mb-s">{{ t('documents') }}</p>
              <div v-if="documentNames.length === 0" class="typography-body-2 text-neutral-1">
                -
              </div>
              <div v-else>
                <div ref="docsContainerRef" class="flex flex-wrap gap-s max-h-14 overflow-hidden">
                  <OLabel
                    v-for="(name, index) in documentNames"
                    :key="index"
                    :label="name"
                    overflow-tooltip
                    :style="docsLabelWidth ? { maxWidth: `${docsLabelWidth}px` } : undefined"
                    class="min-w-0 flex-none max-w-[33%]"
                  />
                </div>
                <button
                  v-if="docsOverflowCount > 0"
                  class="typography-body-2-link text-primary-interactive underline cursor-pointer mt-s"
                  @click="goToTab('knowledge')"
                  data-testid="knowledge-see-all"
                >
                  {{ t('and_x_others', [docsOverflowCount]) }}
                </button>
              </div>
            </div>
          </section>
        </template>

        <ODivider />

        <!-- 5. Call limits -->
        <section class="py-l space-y-l">
          <h3 class="typography-body-1-medium text-neutral-2">
            {{ sectionNumbers.callLimits }}. {{ t('call_limits') }}
          </h3>
          <div class="space-y-l">
            <div>
              <p class="typography-body-2-strong text-neutral-1 mb-s">
                {{ t('inactivity_timeout') }}
              </p>
              <p class="typography-body-2 text-neutral-1">
                {{ config.settings?.inactivity?.user_away_timeout ?? '-' }} {{ t('seconds') }}
              </p>
            </div>
            <div>
              <p class="typography-body-2-strong text-neutral-1 mb-s">{{ t('max_retries') }}</p>
              <p class="typography-body-2 text-neutral-1">
                {{ config.settings?.inactivity?.max_retries_user_away ?? '-' }} {{ t('times') }}
              </p>
            </div>
            <div>
              <p class="typography-body-2-strong text-neutral-1 mb-s">
                {{ t('maximum_call_duration') }}
              </p>
              <p class="typography-body-2 text-neutral-1">
                {{ config.settings?.call_duration_limits?.max_call_duration_seconds ?? '-' }}
                {{ t('seconds') }}
              </p>
            </div>
          </div>
        </section>
      </div>
    </template>

    <template #footer>
      <OButton
        variant="secondary"
        color="neutral"
        :label="t('cancel')"
        @mousedown.prevent
        @click="open = false"
        size="L"
        data-testid="agent-details-close"
      />
      <OButton
        :label="t('edit')"
        @click="goToTab('general-information')"
        size="L"
        data-testid="agent-details-edit"
      />
    </template>
  </ORightPanel>
</template>
